/**
 * Checkout protocol (Neel Slack DM 2026-10-19) — "coliviq AI" reject
 * composer, shared between the admin tenant profile's Move-out tab and the
 * colivIQ Brain card (per the approved mockup: "Reject & email opens the
 * composer ... inline on the card" — the SAME composer, not a new one).
 *
 * Deterministic templates, not a live LLM call — see
 * move-out-reject-templates.ts for why. "Redraft" just recomposes from the
 * current reason + context; the context text is inserted as typed.
 */
import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatDateNumeric } from "@/lib/date-format";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import {
  MOVE_OUT_REJECT_REASONS,
  moveOutRejectTemplate,
  type MoveOutRejectReason,
} from "@/lib/move-out-reject-templates";
import { sendMoveOutRejectEmailAndMarkRejected } from "@/lib/move-out-request-actions";
import {
  actionBtn,
  CHIP,
  SectionIcon,
} from "@/components/admin/v2/tenant/tenant-profile-ui";

// send-admin-email resolves the sender by name; this is what sender:
// "kontakt" actually sends as (same constant BrainEmailComposer.tsx uses).
const MOVE_OUT_REJECT_FROM = "kontakt@coliviq.de";

export function MoveOutRejectComposer({
  open,
  onOpenChange,
  requestId,
  requestedDate,
  tenantEmail,
  tenantName,
  onRejected,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  requestId: string | null;
  /** The pending request's `requested_date` (YYYY-MM-DD). */
  requestedDate: string | null;
  tenantEmail: string;
  tenantName: string;
  /** Called once the email has sent and the row is marked rejected. */
  onRejected: () => void;
}) {
  const { t, language } = useLanguage();
  const [reason, setReason] = useState<MoveOutRejectReason>("room_relet");
  const [context, setContext] = useState("");
  const [lang, setLang] = useState<"de" | "en">("de");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [dirty, setDirty] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const dateLabel = requestedDate
    ? formatDateNumeric(requestedDate, language)
    : "";
  const firstName = tenantName.split(" ")[0] || tenantEmail;

  const draft = (r: MoveOutRejectReason, l: "de" | "en", c: string) => {
    if (!requestedDate) return;
    const tpl = moveOutRejectTemplate(r, l, {
      tenantFirstName: firstName,
      requestedDateLabel: dateLabel || requestedDate,
      context: c,
    });
    setSubject(tpl.subject);
    setBody(tpl.body);
    setDirty(false);
  };

  // Reset to a fresh draft every time the composer opens for a (new) request.
  useEffect(() => {
    if (!open) return;
    setReason("room_relet");
    setContext("");
    setLang("de");
    draft("room_relet", "de", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId]);

  async function send() {
    if (!requestId) return;
    setBusy(true);
    try {
      const { emailSent, requestRowUpdated } =
        await sendMoveOutRejectEmailAndMarkRejected({
          requestId,
          toEmail: tenantEmail,
          subject,
          body,
        });
      if (!emailSent) {
        toast.warning(
          t(
            "admin.tenantTabs.moveOut.toastRejectSkippedDecided",
            "This request was already decided elsewhere — no email was sent."
          )
        );
      } else {
        toast.success(
          requestRowUpdated
            ? t(
                "admin.tenantTabs.moveOut.toastRejected",
                "Request rejected — email sent."
              )
            : t(
                "admin.tenantTabs.moveOut.toastRejectedRaced",
                "Email sent — but this request was already decided elsewhere."
              )
        );
      }
      onOpenChange(false);
      onRejected();
    } catch (e) {
      const m = await edgeFunctionErrorMessage(
        e,
        t(
          "admin.tenantTabs.moveOut.toastRejectFailed",
          "Could not reject the request."
        )
      );
      toast.error(m);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <DialogTitle>
                {t(
                  "admin.tenantTabs.moveOut.rejectTitle",
                  "Reject requested move-out · {date}"
                ).replace("{date}", dateLabel)}
              </DialogTitle>
              <span style={CHIP.rose}>
                {t("admin.tenantTabs.moveOut.rejecting", "Rejecting")}
              </span>
            </div>
            <DialogDescription>
              {t(
                "admin.tenantTabs.moveOut.rejectSubtitle",
                "{firstName} gets this email and the request button reappears on their dashboard."
              ).replace("{firstName}", firstName)}
            </DialogDescription>
          </DialogHeader>

          {/* coliviq AI assist — reason chips + context + Redraft */}
          <div
            style={{
              background: "#F0FAF8",
              border: "1px solid #CDECE7",
              borderRadius: 16,
              padding: "16px 18px",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <SectionIcon bg="#fff" color="#0B7268" size={30} radius={9}>
                <Sparkles size={16} />
              </SectionIcon>
              <span
                style={{ fontSize: 13.5, fontWeight: 700, color: "#0B5A52" }}
              >
                {t("admin.tenantTabs.moveOut.assistName", "coliviq AI")}
              </span>
              <span
                style={{ fontSize: 12.5, color: "#0B7268", fontWeight: 500 }}
              >
                {t(
                  "admin.tenantTabs.moveOut.assistHint",
                  "Pick a reason, add context, Assist writes the reply."
                )}
              </span>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {MOVE_OUT_REJECT_REASONS.map((r) => {
                const selected = reason === r.id;
                return (
                  <button
                    key={r.id}
                    onClick={() => {
                      setReason(r.id);
                      draft(r.id, lang, context);
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      fontSize: 12,
                      fontWeight: selected ? 700 : 600,
                      padding: "5px 11px",
                      borderRadius: 999,
                      cursor: "pointer",
                      background: selected ? "#0F4C4A" : "#fff",
                      color: selected ? "#fff" : "#0B5A52",
                      border: `1px solid ${selected ? "#0F4C4A" : "#CDECE7"}`,
                    }}
                  >
                    {r.label[lang]}
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
              <input
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder={t(
                  "admin.tenantTabs.moveOut.contextPlaceholder",
                  "Add context for the reply…"
                )}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "1px solid #CDECE7",
                  borderRadius: 10,
                  background: "#fff",
                  padding: "9px 12px",
                  fontSize: 13.5,
                  color: "#0F172A",
                }}
              />
              <button
                onClick={() => draft(reason, lang, context)}
                style={{
                  border: "none",
                  background: "#0B7268",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "0 14px",
                  fontWeight: 700,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                }}
              >
                {t("admin.tenantTabs.moveOut.redraft", "Redraft")}
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <div style={{ fontSize: 12, color: "#5C6E6C" }}>
              <div>
                {t("admin.tenantTabs.sepa.from", "From")}:{" "}
                {MOVE_OUT_REJECT_FROM}
              </div>
              <div>
                {t("admin.tenantTabs.moveOut.to", "To")}: {tenantEmail}
              </div>
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {(["de", "en"] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => {
                    setLang(l);
                    if (!dirty) draft(reason, l, context);
                  }}
                  style={{
                    height: 32,
                    padding: "0 12px",
                    borderRadius: 10,
                    border: "none",
                    background: lang === l ? "#F3F0EA" : "transparent",
                    color: "#1A2B2A",
                    fontWeight: 500,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <Input
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setDirty(true);
            }}
            aria-label={t("admin.tenantTabs.moveOut.subject", "Subject")}
          />
          <Textarea
            rows={7}
            value={body}
            onChange={(e) => {
              setBody(e.target.value);
              setDirty(true);
            }}
            aria-label={t("admin.tenantTabs.moveOut.message", "Message")}
          />
          <DialogFooter>
            <button
              disabled={busy}
              onClick={() => onOpenChange(false)}
              style={actionBtn("#64748B")}
            >
              {t("admin.tenantProfile.cancel", "Cancel")}
            </button>
            <button
              disabled={busy || !subject.trim() || !body.trim()}
              onClick={() => setSendConfirmOpen(true)}
              style={{
                ...actionBtn("#0F4C4A"),
                border: "1px solid #0F4C4A",
                background: "#0F4C4A",
                color: "#fff",
              }}
            >
              {t(
                "admin.tenantTabs.moveOut.sendAndReject",
                "Send & reject request"
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* "Send this email?" — same confirm-before-send every Brain email asks for. */}
      <AlertDialog open={sendConfirmOpen} onOpenChange={setSendConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(
                "admin.tenantTabs.moveOut.sendConfirmTitle",
                "Send this email?"
              )}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ whiteSpace: "pre-line" }}>
              {`${t("admin.tenantTabs.sepa.from", "From")}: ${MOVE_OUT_REJECT_FROM}\n${t("admin.tenantTabs.moveOut.to", "To")}: ${tenantEmail}\n${subject}`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>
              {t("admin.tenantProfile.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={() => {
                setSendConfirmOpen(false);
                void send();
              }}
            >
              {t(
                "admin.tenantTabs.moveOut.sendAndReject",
                "Send & reject request"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
